import { createFileRoute } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import type { ParseKeys } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  applicationFilterQuery,
  ROLES,
  STATUSES,
} from '@/admin/applications/applicationFilters';
import type { BadgeVariant } from '@/admin/ui/Badge';
import { Badge } from '@/admin/ui/Badge';
import { createTablePage } from '@/admin/ui/createTablePage';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import i18n from '~/i18n';
import type { FilterField } from '~/styles/tables/filterandSettingsStyles';

type HelperApplication = components['schemas']['HelperApplicationSummary'];
type HelperApplicationRole = components['schemas']['HelperApplicationRole'];
type ApplicationStatus = components['schemas']['ApplicationStatus'];

const statusVariant: Record<ApplicationStatus, BadgeVariant> = {
  approved: 'success',
  rejected: 'error',
  pending: 'warning',
  discarded: 'neutral',
};

// The queue now holds three intake forms, not one: GF 4 (study helper), GF 12
// (follow-up coordinator) and GF 14 (follow-up partner). Spellings follow the
// two labels `web` already ships for the same polymorphic identities
// (`web/public/locales/en/translation.json:286,1134`); admin keeps its own
// English-only `admin.applications.role.*` copies, translated at render.
const roleLabels = {
  STUDY_HELPER: 'admin.applications.role.studyHelper',
  FOLLOWUP_COORDINATOR: 'admin.applications.role.followupCoordinator',
  FOLLOWUP_PARTNER: 'admin.applications.role.followupPartner',
} as const satisfies Record<HelperApplicationRole, ParseKeys>;

const columnHelper = createColumnHelper<HelperApplication>();

const columns = [
  columnHelper.accessor('id', {
    header: () => i18n.t('admin.common.id'),
  }),
  columnHelper.accessor('role', {
    header: () => i18n.t('admin.applications.col.role'),
    cell: (info) => i18n.t(roleLabels[info.getValue()]),
  }),
  columnHelper.accessor((row) => `${row.first_name} ${row.last_name}`, {
    id: 'name',
    header: () => i18n.t('admin.common.name'),
  }),
  columnHelper.accessor('email', {
    header: () => i18n.t('admin.common.email'),
  }),
  columnHelper.accessor('city', {
    header: () => i18n.t('admin.common.city'),
    cell: (info) => info.getValue() || '—',
  }),
  columnHelper.accessor('country', {
    header: () => i18n.t('admin.common.country'),
    cell: (info) => info.getValue() || '—',
  }),
  columnHelper.accessor('congregation_name', {
    header: () => i18n.t('admin.applications.col.congregation'),
    cell: (info) => info.getValue() || '—',
  }),
  columnHelper.accessor('created_at', {
    header: () => i18n.t('admin.applications.col.created'),
    cell: (info) => new Date(info.getValue()).toLocaleDateString(),
  }),
  columnHelper.accessor('status', {
    header: () => i18n.t('admin.common.status'),
    // Lowercase on purpose: the web specs match the badge text.
    cell: (info) => (
      <Badge $variant={statusVariant[info.getValue()]}>{info.getValue()}</Badge>
    ),
  }),
  columnHelper.accessor('agreements_accepted', {
    header: () => i18n.t('admin.applications.col.agreements'),
    cell: (info) => (
      <Badge $variant={info.getValue() ? 'success' : 'neutral'}>
        {info.getValue()
          ? i18n.t('admin.common.yes')
          : i18n.t('admin.common.no')}
      </Badge>
    ),
  }),
];

// Both filters are closed enums, so the panel's options are declared here
// rather than served from a list registry. Status is empty until chosen and
// an empty Status means the pending queue (`applicationFilterQuery`).
function useApplicationFilterFields(): FilterField[] {
  const { t } = useTranslation();
  return [
    {
      key: 'status',
      label: t('admin.common.status'),
      type: 'multiselect',
      options: STATUSES.map((value) => ({
        value,
        label: t(`admin.applications.status.${value}`),
      })),
    },
    {
      key: 'role',
      label: t('admin.applications.col.role'),
      type: 'multiselect',
      options: ROLES.map((value) => ({ value, label: t(roleLabels[value]) })),
    },
  ];
}

const ApplicationsList = createTablePage<
  HelperApplication,
  '/admin/applications/$applicationId'
>({
  name: 'admin.nav.applications',
  searchable: true,
  searchPlaceholder: 'admin.applications.searchPlaceholder',
  // Column ids on the left, the API's sort keys on the right.
  sortKeys: {
    created_at: 'created_at',
    name: 'name',
    congregation_name: 'congregation',
  },
  defaultSort: { key: 'created_at', order: 'desc' },
  filterFields: useApplicationFilterFields,
  useData: (_page, search, { paging, sort, filters }) =>
    api.listHelperApplications.useQuery({
      query: {
        limit: paging.limit,
        offset: paging.offset,
        ...(search ? { search } : {}),
        ...(sort ? { sort: sort.key, order: sort.order } : {}),
        ...applicationFilterQuery(filters),
      },
    }),
  columns: columns,
  itemBaseUrl: '/admin/applications/$applicationId',
  getParams: (row) => ({ applicationId: String(row.id) }),
});

export const Route = createFileRoute('/admin/applications/')({
  staticData: { minTier: 'FRONT_END_SUPPORT' },
  component: ApplicationsList,
});
